/**
 * WfConfigCompare — every walk-forward run launched from one saved config,
 * across helms, lined up side by side.
 *
 * Runs are only comparable when their parameters match, which is exactly what
 * a saved config guarantees: same period, training window, re-rank interval,
 * funds, top-K and rank metric. What's left varying is the helm itself.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MetricInfo from '../components/MetricInfo';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { NativeSelect } from '../components/ui/native-select';
import { Skeleton } from '../components/ui/skeleton';
import { Loader2 } from 'lucide-react';

// Categorical slots, assigned in fixed order and never cycled — colour follows
// the run, not its rank, so re-sorting the table never repaints a series.
const SERIES = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)',
                'var(--series-4)', 'var(--series-5)', 'var(--series-6)'];

function fmtDate(s) { return s ? s.slice(0, 10) : '—'; }

function fmtPct(v, signed = false) {
  if (v == null) return '—';
  return `${signed && v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function fmtNum(v) {
  return v != null ? v.toFixed(2) : '—';
}

function fmtMoney(v) {
  if (v == null) return '—';
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (v >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

// Metric columns, each with how to read it and which direction is better.
//
// Sharpe and Sortino come first among the performance columns on purpose: the
// ranker picks each window's pair on a risk-adjusted metric, so comparing runs
// on raw return rewards whichever one happened to take more risk. Exposure is
// here for the same reason the result page shows it — these ratios blend
// trading quality with time in the market, so two runs are only comparable on
// them once you can see how much each was deployed.
const METRICS = [
  { key: 'sharpe_ratio',      label: 'Sharpe',   best: 'max', info: 'sharpe_ratio',       fmt: v => fmtNum(v) },
  { key: 'sortino_ratio',     label: 'Sortino',  best: 'max', info: 'sortino_ratio',      fmt: v => fmtNum(v) },
  { key: 'total_return_pct',  label: 'Return',   best: 'max', info: 'total_return_pct',   fmt: v => fmtPct(v, true) },
  {
    key: 'cagr_pct', label: 'CAGR', best: 'max', info: 'cagr_pct',
    // Runs spanning under a year are not extrapolated to an annual rate, so
    // the value is the period return and is marked to say so.
    fmt: (v, sm) => (v == null ? '—' : `${fmtPct(v, true)}${sm?.cagr_is_annualized === false ? ' (period)' : ''}`),
  },
  { key: 'max_drawdown_pct',  label: 'Max DD',   best: 'min', info: 'max_drawdown_pct',   fmt: v => v == null ? '—' : `-${v.toFixed(2)}%` },
  { key: 'time_in_market_pct', label: 'Exposure', best: null, info: 'time_in_market_pct', fmt: v => v == null ? '—' : `${v.toFixed(0)}%` },
  { key: 'win_rate_pct',      label: 'Win Rate', best: 'max', info: 'win_rate_pct',       fmt: v => v == null ? '—' : `${v.toFixed(1)}%` },
  { key: 'total_trades',      label: 'Trades',   best: null,  fmt: v => v ?? '—' },
  { key: 'n_windows',         label: 'Windows',  best: null,  fmt: v => v ?? '—' },
  { key: 'n_pair_switches',   label: 'Switches', best: null,  fmt: v => v ?? '—' },
];

function bestValues(runs) {
  const out = {};
  for (const m of METRICS) {
    if (!m.best) continue;
    const vals = runs.map(r => r.summary?.[m.key]).filter(v => v != null);
    if (vals.length < 2) continue;   // nothing to win against
    out[m.key] = m.best === 'max' ? Math.max(...vals) : Math.min(...vals);
  }
  return out;
}

function StatusPill({ status }) {
  const tone = status === 'completed' ? 'bg-win/10 text-win'
    : status === 'failed' ? 'bg-destructive/10 text-destructive'
    : 'bg-surface-card text-muted-foreground border border-border';
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold ${tone}`}>{status}</span>;
}

/**
 * Overlay of each run's equity, indexed to its starting funds so the lines
 * share one axis regardless of capital. Dates are merged across runs; a run
 * without a point on a date leaves a gap rather than a fabricated value.
 */
function EquityOverlay({ runs }) {
  const plotted = runs.filter(r => r.summary?.equity_curve?.length);
  if (plotted.length === 0) return null;

  const byDate = new Map();
  plotted.forEach((run) => {
    const base = run.initial_funds;
    (run.summary.equity_curve || []).forEach((pt) => {
      const d = String(pt.date).slice(0, 10);
      if (!byDate.has(d)) byDate.set(d, { date: d });
      byDate.get(d)[run.helm_name] = base ? ((pt.value - base) / base) * 100 : null;
    });
  });
  const data = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="h-72 w-full text-xs">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeWidth={1} strokeOpacity={0.5} stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={48}
                 tick={{ fill: 'hsl(var(--muted-foreground))' }} tickFormatter={d => d.slice(0, 7)} />
          <YAxis tickLine={false} axisLine={false} width={56}
                 tick={{ fill: 'hsl(var(--muted-foreground))' }} tickFormatter={v => `${v.toFixed(0)}%`} />
          <Tooltip
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '3 3' }}
            labelStyle={{ color: 'hsl(var(--foreground))' }}
            contentStyle={{ background: 'hsl(var(--background))', borderRadius: '8px', border: 'none',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            formatter={(v, name) => [v == null ? '—' : `${v.toFixed(2)}%`, name]}
          />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          {plotted.map((run, i) => (
            <Line
              key={run.id}
              type="monotone"
              dataKey={run.helm_name}
              stroke={SERIES[i % SERIES.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--background))' }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function WfConfigCompare() {
  const { configId } = useParams();
  const navigate = useNavigate();

  const [configs, setConfigs] = useState([]);
  const [config,  setConfig]  = useState(null);
  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    api.get('/wf-configs').then(r => setConfigs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.get(`/wf-configs/${configId}/runs`)
      .then(r => { setConfig(r.data.config); setRuns(r.data.runs); })
      .catch(err => setError(err.response?.status === 404
        ? 'This saved config no longer exists.'
        : (err.response?.data?.error || 'Failed to load comparison')))
      .finally(() => setLoading(false));
  }, [configId]);

  const cfg  = config?.config || {};
  const best = bestValues(runs);

  const paramChips = [
    ['Period',    `${fmtDate(cfg.wf_start)} → ${fmtDate(cfg.wf_end)}`],
    ['Training',  `${cfg.training_days} days`],
    ['Re-rank',   `every ${cfg.interval_days} days`],
    ['Funds',     fmtMoney(cfg.initial_funds)],
    ['Top K',     cfg.top_k],
    ['Rank by',   cfg.rank_by],
  ];

  return (
    <AppLayout title={config?.name || 'Run config'} backTo="/helms" backLabel="Helms">
      <div className="flex flex-col gap-6 p-4 sm:p-6 max-w-6xl">

        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold text-foreground">{config?.name || 'Run config'}</h2>
            <p className="text-sm text-muted-foreground">
              Every walk-forward run launched from this config — same parameters, so the helms are what differ.
            </p>
          </div>
          {configs.length > 1 && (
            <div className="w-full sm:w-64">
              <NativeSelect value={configId} onChange={e => navigate(`/wf-configs/${e.target.value}`)}>
                {configs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </NativeSelect>
            </div>
          )}
        </div>

        {loading && <Skeleton className="h-64 w-full" />}

        {!loading && error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
        )}

        {!loading && !error && (<>
          {/* Config parameters — the constants behind the comparison */}
          <div className="flex flex-wrap gap-2">
            {paramChips.map(([k, v]) => (
              <span key={k} className="flex flex-col gap-0.5 px-3 py-2 rounded-lg border border-border bg-surface-card">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground">{k}</span>
                <span className="text-sm text-foreground">{v ?? '—'}</span>
              </span>
            ))}
          </div>

          {runs.length === 0 && (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
              No runs have used this config yet. Open a helm's Backtesting tab, pick
              “{config?.name}” from Saved Config, and run it — do that on a second helm
              and the two land here side by side.
            </div>
          )}

          {runs.length > 0 && (<>
            {runs.some(r => r.summary?.equity_curve?.length > 0) && (
              <section className="flex flex-col gap-3">
                <h3 className="text-sm font-semibold text-foreground">Equity by helm</h3>
                <p className="text-xs text-muted-foreground -mt-2">Indexed to starting funds — % return.</p>
                <div className="rounded-lg border border-border p-3 bg-surface-card">
                  <EquityOverlay runs={runs} />
                </div>
              </section>
            )}

            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-foreground">
                {runs.length} run{runs.length === 1 ? '' : 's'} across {new Set(runs.map(r => r.helm_id)).size} helm{new Set(runs.map(r => r.helm_id)).size === 1 ? '' : 's'}
              </h3>

              {/* DESKTOP: comparison table */}
              <div className="hidden md:block rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead className="border-b border-border bg-surface-card">
                      <tr>
                        {[
                          { label: 'Helm' }, { label: 'Run' }, { label: 'Status' },
                          ...METRICS, { label: 'Dominant Pair' }, { label: '' },
                        ].map(({ label, info }, i) => (
                          <th key={i} className="px-3 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                            <span className="flex items-center gap-1">{label}{info && <MetricInfo metric={info} />}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((r, i) => {
                        const sm = r.summary || {};
                        return (
                          <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-surface-hover/40">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className="flex items-center gap-2">
                                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: SERIES[i % SERIES.length] }} />
                                <span className="font-medium text-foreground">{r.helm_name}</span>
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</td>
                            <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                            {METRICS.map(m => {
                              const v = sm[m.key];
                              const isBest = m.best && v != null && v === best[m.key];
                              return (
                                <td key={m.key} className={`px-3 py-2 text-sm whitespace-nowrap ${isBest ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                                  {m.fmt(v, sm)}
                                  {isBest && <span className="ml-1 text-[10px] text-win">best</span>}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-xs text-muted-foreground max-w-[220px] truncate" title={sm.top_pair || ''}>
                              {sm.top_pair ? `${sm.top_pair} (${sm.top_pair_windows}w)` : '—'}
                            </td>
                            <td className="px-3 py-2">
                              <Button size="sm" variant="ghost" className="h-7"
                                      onClick={() => navigate(`/helms/${r.helm_id}/wf-runs/${r.id}`)}>
                                {r.status === 'completed' ? 'View' : 'Watch'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* MOBILE: card list */}
              <div className="flex flex-col gap-3 md:hidden">
                {runs.map((r, i) => {
                  const sm = r.summary || {};
                  return (
                    <div key={r.id} className="border border-border rounded-lg p-3 bg-surface-card flex flex-col gap-3">
                      <div className="flex justify-between items-center gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: SERIES[i % SERIES.length] }} />
                          <span className="font-medium text-foreground truncate">{r.helm_name}</span>
                        </span>
                        <StatusPill status={r.status} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 bg-surface-base border border-border rounded p-2">
                        {METRICS.map(m => (
                          <span key={m.key} className="flex flex-col">
                            <span className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
                              {m.label}{m.info && <MetricInfo metric={m.info} />}
                            </span>
                            <span className={`text-sm ${m.best && sm[m.key] != null && sm[m.key] === best[m.key] ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                              {m.fmt(sm[m.key], sm)}
                            </span>
                          </span>
                        ))}
                      </div>
                      {sm.top_pair && (
                        <span className="text-xs text-muted-foreground">Dominant pair: {sm.top_pair} ({sm.top_pair_windows}w)</span>
                      )}
                      <Button size="sm" variant="outline" className="w-full"
                              onClick={() => navigate(`/helms/${r.helm_id}/wf-runs/${r.id}`)}>
                        {r.status === 'completed' ? 'View Results' : 'Watch Progress'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {runs.some(r => ['pending', 'running'].includes(r.status)) && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  A run is still in progress — its metrics fill in as windows complete.
                </p>
              )}
            </section>
          </>)}
        </>)}
      </div>
    </AppLayout>
  );
}
